import { useCallback, useEffect, useState } from 'react';
import styles from './spacesOfAttribute.module.scss';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';
import { LoadingCircle } from 'components/LoadingCircle/LoadingCircle';
import { SpaceAttributeMap } from 'components/CampaignLimitation/CustomOutdoorDevice/DeviceDataContext';

/**
 * POS-7996：hover 清單過濾用的選項。戶外門市/裝置模式會帶入，讓 fallback fetch
 * 只列「可投遞」版位（排除 BANNED 等已下架）並依 extra（門市 key）去重，與旁邊的
 * 「門市數量」一致。其他呼叫端（如 Beacon）不帶，行為維持原樣。
 */
type DeliverableFilterOptions = {
  extraGetter?: (record: any) => string | undefined;
  testMode?: boolean;
  deliverableExtras?: Set<string>;
};

type SpacesOfAttributeProps = {
  spaceAttributeMap: SpaceAttributeMap;
  attributeType: string;
  spaceAttribute: string;
  adFormat: string;
  channelGroupId: number;
  showId: boolean;
} & DeliverableFilterOptions;

const adRequestSourceManager: AdRequestSourceManager =
  new DefaultAdRequestSourceManager();

export const SpacesOfAttribute: React.FC<SpacesOfAttributeProps> = ({
  spaceAttributeMap,
  attributeType,
  spaceAttribute,
  adFormat,
  channelGroupId,
  showId,
  extraGetter,
  testMode,
  deliverableExtras
}) => {
  const [spaces, setSpaces] = useState<SelectOptions[]>();

  useEffect(() => {
    (async () => {
      if (
        spaceAttributeMap[attributeType] &&
        spaceAttributeMap[attributeType][spaceAttribute]
      ) {
        // 地圖 overlay 開啟後才建的 spaceAttributeMap 已在 fetchSpaceAndUpdateMaps
        // 依可投遞狀態過濾並依門市去重，直接沿用即可。
        setSpaces(spaceAttributeMap[attributeType][spaceAttribute]);
        return;
      }
      const fetched = await adRequestSourceManager.getSpacesWithConditions(
        false,
        { [attributeType]: [spaceAttribute] },
        adFormat,
        channelGroupId,
        extraGetter,
        testMode
      );
      // POS-7996：帶了 extraGetter 與 deliverableExtras 時，只保留可投遞門市並依
      // extra 去重；沒帶（如 Beacon）則維持原本「逐筆列出」的行為。
      if (extraGetter && deliverableExtras) {
        const seen = new Set<string>();
        setSpaces(
          fetched.filter((space) => {
            const extra = space.extra?.toString();
            if (!extra || !deliverableExtras.has(extra)) return false;
            if (seen.has(extra)) return false;
            seen.add(extra);
            return true;
          })
        );
        return;
      }
      setSpaces(fetched);
    })();
  }, [
    spaceAttributeMap,
    attributeType,
    spaceAttribute,
    adFormat,
    channelGroupId,
    extraGetter,
    testMode,
    deliverableExtras
  ]);

  const renderSpaces = useCallback(
    (spaces: SelectOptions[]) => {
      const items = spaces.map((space) => (
        <li key={space.value}>
          <div>{space.label}</div>
          {showId && (
            <div>{`ID: ${space.value.toString().replace('\\', '')}`}</div>
          )}
        </li>
      ));
      return <ul>{items}</ul>;
    },
    [showId]
  );

  return (
    <div className={styles.spacesOfAttribute}>
      {spaces ? renderSpaces(spaces) : <LoadingCircle width={15} height={15} />}
    </div>
  );
};

export const renderSpacesOfAttribute = (
  spaceAttributeMap: SpaceAttributeMap,
  attributeType: string,
  spaceAttribute: string,
  adFormat: string,
  channelGroupId: number,
  showId: boolean,
  deliverableFilter?: DeliverableFilterOptions
) => {
  return (
    <SpacesOfAttribute
      spaceAttributeMap={spaceAttributeMap}
      attributeType={attributeType}
      spaceAttribute={spaceAttribute}
      adFormat={adFormat}
      channelGroupId={channelGroupId}
      showId={showId}
      extraGetter={deliverableFilter?.extraGetter}
      testMode={deliverableFilter?.testMode}
      deliverableExtras={deliverableFilter?.deliverableExtras}
    />
  );
};
